/**
 * TapPayCheckoutModal
 *
 * TODO: Replace hardcoded strings with i18n translations prop (same pattern as PurchaseSuccessModal)
 *
 * Handles inline TapPay credit card payments inside a modal.
 *
 * Usage:
 *   <TapPayCheckoutModal
 *     config={{ app_id: 12345, app_key: 'tpkey_xxx', env: 'sandbox' }}
 *     orderId={42}
 *     amount={3000}
 *     currency="TWD"
 *     onSuccess={() => window.location.href = '?purchase=success'}
 *     onClose={() => setTapPayCheckout(null)}
 *   />
 */

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { trackEvent } from 'utils/analytics'

const TAPPAY_SDK_URL = 'https://js.tappaysdk.com/sdk/tpdirect/v5.19.2'

const cardViewStyle = {
  color: '#222222',
  fontSize: '16px',
  lineHeight: '24px',
  fontWeight: '400',
  errorColor: '#E53E3E',
  placeholderColor: '#A0AEC0',
}

const FIELD_STATUS = {
  EMPTY: 0,
  VALID: 1,
  INVALID: 2,
}

function loadTapPaySDK() {
  return new Promise((resolve, reject) => {
    if (window.TPDirect) {
      resolve(window.TPDirect)
      return
    }

    const existingScript = document.querySelector(`script[src="${TAPPAY_SDK_URL}"]`)
    if (existingScript) {
      // Script already injected but TPDirect not yet ready — wait for load
      existingScript.addEventListener('load', () => {
        if (window.TPDirect) resolve(window.TPDirect)
        else reject(new Error('TapPay SDK loaded but TPDirect is not available'))
      })
      existingScript.addEventListener('error', () => reject(new Error('Failed to load TapPay SDK')))
      return
    }

    const script = document.createElement('script')
    script.src = TAPPAY_SDK_URL
    script.async = true
    script.onload = () => {
      if (window.TPDirect) resolve(window.TPDirect)
      else reject(new Error('TapPay SDK loaded but TPDirect is not available'))
    }
    script.onerror = () => reject(new Error('Failed to load TapPay SDK. Please check your internet connection and try again.'))
    document.head.appendChild(script)
  })
}

export default function TapPayCheckoutModal({ config, orderId, amount, currency, onSuccess, onClose }) {
  const [sdkState, setSdkState] = useState('loading') // 'loading' | 'ready' | 'error'
  const [sdkError, setSdkError] = useState(null)

  const [cardStatus, setCardStatus] = useState({
    number: FIELD_STATUS.EMPTY,
    expiry: FIELD_STATUS.EMPTY,
    ccv: FIELD_STATUS.EMPTY,
  })
  const [canSubmit, setCanSubmit] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [paymentError, setPaymentError] = useState(null)

  const cardElementRef = useRef(null)
  const sdkInitialized = useRef(false)

  const initSDK = useCallback(async () => {
    setSdkState('loading')
    setSdkError(null)

    try {
      const TPDirect = await loadTapPaySDK()

      TPDirect.setupSDK(config.app_id, config.app_key, config.env)

      // Give the DOM a tick to render the card element container
      await new Promise(r => setTimeout(r, 50))

      if (!cardElementRef.current) {
        throw new Error('Card element container not found')
      }

      TPDirect.card.setup({
        fields: {
          number: { element: '#tappay-card-number', placeholder: '•••• •••• •••• ••••' },
          expirationDate: { element: '#tappay-card-expiry', placeholder: 'MM / YY' },
          ccv: { element: '#tappay-card-ccv', placeholder: 'CVC' },
        },
        styles: {
          'input': { color: cardViewStyle.color, 'font-size': cardViewStyle.fontSize, 'line-height': cardViewStyle.lineHeight },
          ':focus': { color: '#3366FF' },
          '.valid': { color: '#2D7D46' },
          '.invalid': { color: cardViewStyle.errorColor },
          '::placeholder': { color: cardViewStyle.placeholderColor },
        },
        isMaskCreditCardNumber: true,
        maskCreditCardNumberRange: { beginIndex: 6, endIndex: 11 },
      })

      TPDirect.card.onUpdate((update) => {
        setCardStatus({
          number: update.status.number,
          expiry: update.status.expiry,
          ccv: update.status.ccv,
        })
        setCanSubmit(update.canGetPrime)
      })

      sdkInitialized.current = true
      setSdkState('ready')
    } catch (err) {
      setSdkError(err.message || 'Failed to initialize payment SDK')
      setSdkState('error')
    }
  }, [config])

  useEffect(() => {
    initSDK()
  }, [initSDK])

  useEffect(() => {
    trackEvent('checkout_modal_open')
  }, [])

  // Prevent background scroll while modal is open
  useEffect(() => {
    const original = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = original }
  }, [])

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return
    trackEvent('checkout_submitted', { order_id: orderId, amount, currency })
    setPaymentError(null)
    setSubmitting(true)

    try {
      const TPDirect = window.TPDirect
      if (!TPDirect) throw new Error('Payment SDK is not available. Please refresh and try again.')

      const primeResult = await new Promise((resolve, reject) => {
        TPDirect.card.getPrime((result) => {
          if (result.status !== 0) {
            reject(new Error(result.msg || 'Failed to process card information. Please check your card details.'))
          } else {
            resolve(result.card.prime)
          }
        })
      })

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch('/api/tappay/pay', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
        body: JSON.stringify({ prime: primeResult, order_id: orderId }),
      })

      const data = await response.json()

      if (!response.ok) {
        const message = data.errors?.join(', ') || data.message || 'Payment failed. Please try again or use a different card.'
        throw new Error(message)
      }

      trackEvent('checkout_success', { order_id: orderId, amount, currency })
      onSuccess(data)
    } catch (err) {
      trackEvent('checkout_failed', { order_id: orderId, error: err.message })
      setPaymentError(err.message || 'An unexpected error occurred. Please try again.')
      setSubmitting(false)
    }
  }

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !submitting) onClose()
  }

  const fieldBorderClass = (status) => {
    if (status === FIELD_STATUS.VALID) return 'border-green-400'
    if (status === FIELD_STATUS.INVALID) return 'border-red-400'
    return 'border-gray-300'
  }

  const displayAmount = typeof amount === 'number'
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'TWD', minimumFractionDigits: currency === 'TWD' ? 0 : 2 }).format(amount)
    : `${currency || ''} ${amount}`

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={handleOverlayClick}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ backgroundColor: '#E6F0FF' }}>
          <div>
            <h2 className="text-lg font-bold" style={{ color: '#3366FF' }}>
              Secure Payment
            </h2>
            <p className="text-sm mt-0.5" style={{ color: '#3366FF', opacity: 0.75 }}>
              Powered by TapPay
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-40 cursor-pointer"
            aria-label="Close payment modal"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Amount summary */}
          <div className="mb-5 flex items-center justify-between py-3 px-4 rounded-lg bg-gray-50">
            <span className="text-sm text-gray-500">Amount due</span>
            <span className="text-lg font-bold text-gray-900">{displayAmount}</span>
          </div>

          {/* SDK loading state */}
          {sdkState === 'loading' && (
            <div className="flex flex-col items-center justify-center py-10 gap-3">
              <svg className="w-8 h-8 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              <p className="text-sm text-gray-500">Loading payment form...</p>
            </div>
          )}

          {/* SDK error state */}
          {sdkState === 'error' && (
            <div className="flex flex-col items-center gap-4 py-6">
              <div className="flex items-start gap-3 w-full rounded-lg bg-red-50 border border-red-200 p-4">
                <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-red-700">{sdkError}</p>
              </div>
              <button
                onClick={initSDK}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors cursor-pointer"
                style={{ backgroundColor: '#3366FF' }}
                onMouseOver={e => e.currentTarget.style.backgroundColor = '#2952CC'}
                onMouseOut={e => e.currentTarget.style.backgroundColor = '#3366FF'}
              >
                Retry
              </button>
            </div>
          )}

          {/* Card fields — always in DOM so TapPay can mount into them; hidden until SDK ready */}
          <div ref={cardElementRef} style={{ display: sdkState === 'ready' ? 'block' : 'none' }}>
            {/* Card number */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Card Number</label>
              <div className={`border rounded-lg px-3 h-11 flex items-center transition-colors ${fieldBorderClass(cardStatus.number)}`}>
                <div id="tappay-card-number" className="w-full" />
              </div>
            </div>

            {/* Expiry + CVC row */}
            <div className="flex gap-4 mb-5">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Expiry Date</label>
                <div className={`border rounded-lg px-3 h-11 flex items-center transition-colors ${fieldBorderClass(cardStatus.expiry)}`}>
                  <div id="tappay-card-expiry" className="w-full" />
                </div>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">CVC</label>
                <div className={`border rounded-lg px-3 h-11 flex items-center transition-colors ${fieldBorderClass(cardStatus.ccv)}`}>
                  <div id="tappay-card-ccv" className="w-full" />
                </div>
              </div>
            </div>

            {/* Payment error */}
            {paymentError && (
              <div className="mb-4 flex items-start gap-2.5 rounded-lg bg-red-50 border border-red-200 p-3">
                <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-red-700">{paymentError}</p>
              </div>
            )}

            {/* Security note */}
            <div className="flex items-center gap-1.5 mb-5">
              <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <p className="text-xs text-gray-400">Your card details are encrypted and never stored on our servers.</p>
            </div>

            {/* Action buttons */}
            <div className="flex justify-end space-x-3">
              <button
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors disabled:opacity-40 cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!canSubmit || submitting}
                className="px-5 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 cursor-pointer flex items-center gap-2"
                style={{ backgroundColor: canSubmit && !submitting ? '#3366FF' : '#93AEFF' }}
                onMouseOver={e => { if (canSubmit && !submitting) e.currentTarget.style.backgroundColor = '#2952CC' }}
                onMouseOut={e => { if (canSubmit && !submitting) e.currentTarget.style.backgroundColor = '#3366FF' }}
              >
                {submitting && (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                )}
                {submitting ? 'Processing...' : `Pay ${displayAmount}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
